import type {SignalToWeb} from "@khanacademy/embedded-webapp-schema";
import Log from "@khan/logging";

/**
 * Subscribe to window.onmessage events and handle the signals it receives.
 */
export class SignalListener {
    private readonly _unsubscribe: () => void;
    private readonly _listeners = new Map<string, Array<(data: any) => void>>();

    private _getListeners(name: string) {
        let listeners = this._listeners.get(name);
        if (!listeners) {
            listeners = [];
            this._listeners.set(name, listeners);
        }
        return listeners;
    }

    private _tryParseSignal(msg: string): SignalToWeb | void {
        let signal;
        try {
            signal = typeof msg === "string" ? JSON.parse(msg) : msg;
        } catch (e) {
            // The JSON didn't parse. Let's assume this just isn't for us.
            return;
        }

        // Do some minimal checking that we got a valid web message (this
        // should mostly be kept in check by unit tests and shared types).
        // We don't check for `data` as some signals don't have data.
        if ("type" in signal) {
            return signal;
        }
    }

    private _onMessage(event: MessageEvent): void {
        Log.log("onmessage", event.data);

        if (event.data === "undefined" || !event.data) {
            Log.log("ignored: no data");
            return;
        }

        // There's a possibility that something is posting us a message that
        // is not our mobile app signals. So we try parsing the signal, and
        // if it fails, we just ignore it.
        const signal = this._tryParseSignal(event.data);
        if (signal == null) {
            // Doesn't look like it was a signal for us, so we ignore it.
            Log.log("ignored: not parseable");
            return;
        }

        const listeners = this._listeners.get(signal.type);
        if (listeners == null || listeners.length === 0) {
            // No one is listening for this signal, so we ignore it.
            Log.log(`ignored: no listeners for ${signal.type} signal`);
            return;
        }

        // We have a valid looking signal and listeners for that signal,
        // so we invoke all the listeners.
        for (const listener of listeners) {
            listener(signal.data);
        }
    }

    constructor(
        subscribe: (handler: (event: MessageEvent) => void) => () => void,
    ) {
        const boundHandler = (event: MessageEvent) => this._onMessage(event);
        this._unsubscribe = subscribe(boundHandler);
    }

    /**
     * Tear down the listener.
     *
     * This clears all listeners and unsubscribes from window.onmessage making
     * the instance inert and unusable.
     *
     * Providing this so that we can tidy-up between tests.
     *
     * In non-test scenarios, things should be properly cleaning up, or there's
     * a full-page navigation and we don't care. For dev, we throw an error when
     * this is called to let folks know they shouldn't call it. In prod, it's a
     * no-op.
     */
    tearDown() {
        if (process.env.NODE_ENV === "test") {
            this._unsubscribe();
            this._listeners.clear();
        } else if (process.env.NODE_ENV !== "production") {
            throw new Error("tearDown() should only be called in tests");
        }
    }

    /**
     * Listen for a specific message from the mobile app.
     */
    listen<TSignal extends SignalToWeb>(
        name: TSignal["type"],
        callback: (data: TSignal["data"]) => void,
    ): () => void {
        const listeners = this._getListeners(name);
        listeners.push(callback);
        return () => {
            const listeners = this._getListeners(name);
            const idx = listeners.indexOf(callback);
            if (idx !== -1) {
                listeners.splice(idx, 1);
            }
        };
    }
}
